import React, { useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import axios from 'axios';
import css from './CheckoutPlanPage.css';
import config from '../../config';
import { ensureCurrentUser } from '../../util/data';
import { fetchCurrentUser } from '../../ducks/user.duck';
import MainDashboardWrapper from '../../components/MainDashboardWrapper/MainDashboardWrapper';
import { getReferralCode, clearReferralCode } from '../../util/referral';
import { isValidPostalCode } from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';
import { PLAN_CATALOG, resolvePlanId } from '../GetStartedPage2/planCatalog';

// Logged-in sibling of CheckoutPlanPage.js — same embedded Chargebee
// Payment Components checkout, same plan catalog, but for a marketplace
// user who's already signed in. Handles both first-time subscribing
// (tier 0 -> paid) and switching an existing subscription to a different
// plan. Differences from CheckoutPlanPage.js:
//   - email is sourced from the account and locked, not typed in
//   - no account-creation step afterward (the account already exists) —
//     success redirects straight to /welcome?plan=<planId>, same
//     destination StripePayoutPage.js's openChargebeePortal already uses
//   - when the coach already has a subscription (isSwitch below), the
//     backend computes a prorated amount and updates the existing
//     Chargebee subscription in place (subscription.update_for_items)
//     instead of creating a new customer/subscription — see
//     /payment-intent and /checkout/subscribe-with-intent in
//     subscription-service/app.js
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COMPONENT_LOAD_TIMEOUT_MS = 6000;
const COUNTRIES = getCountryCodes(config.locale);

function storageKeyFor(tier) {
  return `checkout-plan-loggedin-saved:${tier}`;
}

function readSavedCheckout(tier, urlCoupon) {
  if (typeof window === 'undefined' || !urlCoupon) return null;
  try {
    const raw = window.sessionStorage.getItem(storageKeyFor(tier));
    if (!raw) return null;
    const saved = JSON.parse(raw);
    return saved && saved.couponCode === urlCoupon ? saved : null;
  } catch (e) {
    return null;
  }
}

function CheckoutPlanLoggedInPage(props) {
  const { currentUser, onFetchCurrentUser, history } = props;
  const ensuredUser = ensureCurrentUser(currentUser);

  const tier = props.params && props.params.tier;
  const period =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('period') === 'annual'
        ? 'annual'
        : 'monthly'
      : 'monthly';
  const urlCoupon =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('coupon') : null;

  const plan = PLAN_CATALOG[tier];
  const planId = resolvePlanId(tier, period);
  const isAnnual = period === 'annual';
  const savedCheckout = readSavedCheckout(tier, urlCoupon);

  const accountEmail = ensuredUser.attributes.email || '';
  const accountFirstName =
    (ensuredUser.attributes.profile && ensuredUser.attributes.profile.firstName) || '';
  const accountLastName =
    (ensuredUser.attributes.profile && ensuredUser.attributes.profile.lastName) || '';

  const emptyForm = {
    firstName: accountFirstName,
    lastName: accountLastName,
    email: accountEmail,
    addressLine1: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  };

  const [lifetime, setLifetime] = useState({ seatsRemaining: null, soldOut: false });
  const [form, setForm] = useState(() => (savedCheckout ? savedCheckout.form : emptyForm));
  const [formErrors, setFormErrors] = useState({});
  const [pageError, setPageError] = useState(null);
  const [cardReady, setCardReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const paymentComponentRef = useRef(null);

  const [couponCode, setCouponCode] = useState(() => (savedCheckout ? savedCheckout.couponCode : ''));
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponError, setCouponError] = useState(null);
  // Set when a coupon is valid but scoped (in Chargebee) to a different
  // plan than this one — [{ tier, period, label }, ...] the coupon IS
  // valid for, so we can offer a direct "switch to this plan" link instead
  // of just saying "invalid".
  const [couponEligiblePlans, setCouponEligiblePlans] = useState(null);
  const [couponPreview, setCouponPreview] = useState(() => (savedCheckout ? savedCheckout.couponPreview : null));
  const appliedCouponCode = couponPreview ? couponCode.trim() : null;

  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);
  const appliedCouponRef = useRef(null);
  useEffect(() => {
    appliedCouponRef.current = appliedCouponCode;
  }, [appliedCouponCode]);

  // Refresh the account once on mount so subscriptionDetails (the
  // already-subscribed guard below) reflects the latest state, same as
  // WelcomePage.js does for its own metadata-dependent render.
  useEffect(() => {
    if (typeof onFetchCurrentUser === 'function') onFetchCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // currentUser is frequently still loading on first render (this page is
  // reached via a full navigation, not a client-side transition — see
  // StripePayoutPage.js's goToLoggedInCheckout), so the lazy useState
  // initializer above can capture an empty accountEmail before the fetch
  // resolves. Sync it in once it's actually available — the locked email
  // field would otherwise stay permanently blank. First/last name only
  // fill in if the coach hasn't already typed something, so this never
  // clobbers their input.
  useEffect(() => {
    setForm(f => {
      if (f.email === accountEmail && (f.firstName || !accountFirstName) && (f.lastName || !accountLastName)) {
        return f;
      }
      return {
        ...f,
        email: accountEmail || f.email,
        firstName: f.firstName || accountFirstName,
        lastName: f.lastName || accountLastName,
      };
    });
  }, [accountEmail, accountFirstName, accountLastName]);

  // A coach who already has a subscription is switching plans, not
  // subscribing for the first time — same field StripePayoutPage.js reads
  // for its own "Switch to X" buttons.
  const subscriptionDetails =
    (ensuredUser.attributes.profile &&
      ensuredUser.attributes.profile.metadata &&
      ensuredUser.attributes.profile.metadata.subscriptionDetails) ||
    null;
  const existingSubscriptionId = subscriptionDetails && subscriptionDetails.subscriptionId;
  const existingPlanId = subscriptionDetails && subscriptionDetails.planId;
  const isSwitch = !!existingSubscriptionId;
  // Nothing to do if they're already on this exact plan — send them back
  // rather than showing a checkout form for a $0/no-op "switch".
  const isNoOpSwitch = isSwitch && existingPlanId === planId;

  useEffect(() => {
    if (isNoOpSwitch && history && history.replace) {
      history.replace('/account/payments/pricing');
    }
  }, [isNoOpSwitch, history]);

  useEffect(() => {
    if (tier !== 'lifetime') return;
    axios
      .post(`${config.subscriptionServiceUrl}eligible-plans`, {})
      .then(res => {
        const data = res.data || {};
        const seatsRemaining = data.lifetime && data.lifetime.seatsRemaining;
        setLifetime({
          seatsRemaining: typeof seatsRemaining === 'number' ? seatsRemaining : null,
          soldOut: seatsRemaining === 0,
        });
      })
      .catch(() => {
        // Best-effort — if this fails we just show the page without a seat count.
      });
  }, [tier]);

  const reloadWithCoupon = (code, preview) => {
    try {
      window.sessionStorage.setItem(
        storageKeyFor(tier),
        JSON.stringify({ form: formRef.current, couponCode: code, couponPreview: preview })
      );
    } catch (e) {
      // Best-effort — worst case the customer just has to retype the form
      // after the reload.
    }
    const params = new URLSearchParams(window.location.search);
    params.set('coupon', code);
    window.location.href = `${window.location.pathname}?${params.toString()}`;
  };

  const applyCoupon = () => {
    const code = couponCode.trim();
    setCouponPreview(null);
    setCouponError(null);
    setCouponEligiblePlans(null);
    if (!code || !planId) return;

    setCouponChecking(true);
    axios
      .post(`${config.subscriptionServiceUrl}coupon-preview`, {
        plan: planId,
        coupon: code,
        // Identifies the coach so backend-only coupons (e.g. YOUWEREEARLY,
        // restricted to accounts created before its cutoff) can check
        // eligibility server-side — this account's own createdAt, never
        // trusted from anywhere client-editable.
        email: accountEmail,
        flexUserUUID: ensuredUser.id && ensuredUser.id.uuid,
      })
      .then(res => {
        setCouponChecking(false);
        const data = res.data || {};
        if (!data.valid) {
          setCouponError(data.message || 'This coupon is not valid.');
          setCouponEligiblePlans(data.eligiblePlans && data.eligiblePlans.length > 0 ? data.eligiblePlans : null);
          return;
        }
        reloadWithCoupon(code, data);
      })
      .catch(() => {
        setCouponChecking(false);
        setCouponError("Couldn't check that coupon right now — please try again.");
      });
  };

  const removeCoupon = () => {
    try {
      window.sessionStorage.removeItem(storageKeyFor(tier));
    } catch (e) {
      // Ignore — the stale entry just won't match next time.
    }
    const params = new URLSearchParams(window.location.search);
    params.delete('coupon');
    const query = params.toString();
    window.location.href = `${window.location.pathname}${query ? `?${query}` : ''}`;
  };

  const formatMoney = (cents, currencyCode) => {
    const amount = (cents / 100).toFixed(2);
    return currencyCode === 'USD' || !currencyCode ? `$${amount}` : `${amount} ${currencyCode}`;
  };

  // Same Chargebee Payment Components mount pattern as CheckoutPlanPage.js
  // (see that file's header comment for why a coupon change reloads the
  // page instead of remounting the component in place).
  useEffect(() => {
    if (!planId || isNoOpSwitch) return;
    if (typeof window === 'undefined' || !window.Chargebee) {
      setPageError("Payment form isn't available right now — please refresh and try again.");
      return;
    }

    let cancelled = false;
    const cbInstance = window.Chargebee.init({
      site: config.chargebeeSiteName,
      publishableKey: config.chargebeePublishableKey,
    });

    const timeoutId = setTimeout(() => {
      if (cancelled || paymentComponentRef.current) return;
      setPageError('Payment form unavailable — please contact support.');
    }, COMPONENT_LOAD_TIMEOUT_MS);

    const finishSubscription = paymentIntentId => {
      const {
        firstName,
        lastName,
        email,
        addressLine1,
        city,
        state,
        zip,
        country,
      } = formRef.current;
      // manualCoupon: typed in and confirmed via /coupon-preview — a
      // failure here must block checkout (see /checkout/subscribe-with-intent).
      // referralCoupon: silently carried over from a past ?ref= link visit,
      // never seen or chosen by this customer — sent separately so the
      // backend can treat its failure (self-referral, expired, deleted) as
      // "no discount" instead of blocking the subscription outright.
      const manualCoupon = appliedCouponRef.current;
      const referralCoupon = manualCoupon ? null : getReferralCode();
      axios
        .post(`${config.subscriptionServiceUrl}checkout/subscribe-with-intent`, {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          addressLine1: addressLine1.trim(),
          city: city.trim(),
          state: state.trim(),
          zip: zip.trim(),
          country: country || 'US',
          plan: planId,
          paymentIntentId,
          flexUserUUID: ensuredUser.id && ensuredUser.id.uuid,
          ...(isSwitch ? { subscriptionId: existingSubscriptionId } : {}),
          ...(manualCoupon ? { coupon: manualCoupon } : {}),
          ...(referralCoupon ? { referralCoupon } : {}),
        })
        .then(res => {
          const data = res.data || {};
          if (!data.ok || !data.subscriptionId) {
            throw new Error(data.message || 'subscribe_failed');
          }
          clearReferralCode();
          window.location.href = `${window.location.origin}/welcome?plan=${encodeURIComponent(planId)}`;
        })
        .catch(err => {
          setSubmitting(false);
          setPageError(
            (err && err.response && err.response.data && err.response.data.message) ||
              (err && err.message) ||
              "We couldn't process that card. Please check the details and try again."
          );
        });
    };

    const createIntent = coupon =>
      axios.post(`${config.subscriptionServiceUrl}payment-intent`, {
        plan: planId,
        // Always sent here (unlike the anonymous CheckoutPlanPage.js,
        // which only sends it alongside a coupon) — this page's email is
        // always known and locked to the account, and the backend uses it
        // to defensively check for an existing live subscription even
        // when isSwitch is wrong because currentUser's cached
        // subscriptionDetails hasn't caught up with Chargebee's webhook
        // yet. See findLiveSubscriptionByEmail in subscription-service.
        email: formRef.current.email.trim(),
        // Lets the backend's YOUWEREEARLY-style eligibility checks resolve
        // the coach's real account by UUID rather than email alone.
        flexUserUUID: ensuredUser.id && ensuredUser.id.uuid,
        ...(isSwitch ? { subscriptionId: existingSubscriptionId } : {}),
        ...(coupon ? { coupon } : {}),
      });

    const mountPaymentComponent = paymentIntent => {
      const components = cbInstance.components({});
      const paymentComponent = components.create(
        'payment',
        {
          paymentIntent,
          layout: { type: 'tab' },
          paymentMethods: { allowed: ['card'] },
        },
        {
          onError: error => {
            setSubmitting(false);
            setPageError(
              (error && error.message) || "We couldn't process that card. Please check the details and try again."
            );
          },
          onSuccess: authorizedIntent => {
            finishSubscription(authorizedIntent.id);
          },
        }
      );

      return paymentComponent.mount('#checkout-plan-loggedin-payment-component').then(mounted => {
        if (cancelled) return;
        if (!mounted) throw new Error('mount_failed');
        paymentComponentRef.current = paymentComponent;
        clearTimeout(timeoutId);
        setCardReady(true);
      });
    };

    createIntent(urlCoupon)
      .catch(err => {
        if (urlCoupon && err && err.response && err.response.data && err.response.data.error === 'invalid_coupon') {
          if (!cancelled) {
            const errData = err.response.data;
            setCouponError(errData.message || 'That coupon could not be applied.');
            setCouponEligiblePlans(errData.eligiblePlans && errData.eligiblePlans.length > 0 ? errData.eligiblePlans : null);
            setCouponPreview(null);
          }
          return createIntent(null);
        }
        throw err;
      })
      .then(res => {
        if (cancelled) return;
        const paymentIntent = res.data && res.data.payment_intent;
        if (!paymentIntent) throw new Error('payment_intent_failed');
        return mountPaymentComponent(paymentIntent);
      })
      .catch(() => {
        if (!cancelled) {
          clearTimeout(timeoutId);
          setPageError('Payment form unavailable — please contact support.');
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, urlCoupon, isNoOpSwitch]);

  const handleSubmit = async e => {
    e.preventDefault();
    setPageError(null);

    const errors = {};
    if (!form.firstName.trim()) errors.firstName = 'Required';
    if (!form.lastName.trim()) errors.lastName = 'Required';
    if (!EMAIL_RE.test(form.email.trim())) errors.email = 'Enter a valid email';
    if (!form.addressLine1.trim()) errors.addressLine1 = 'Required';
    if (!form.city.trim()) errors.city = 'Required';
    if (!form.state.trim()) errors.state = 'Required';
    if (!form.zip.trim()) {
      errors.zip = 'Required';
    } else if (!isValidPostalCode(form.zip.trim())) {
      errors.zip = 'Enter a valid ZIP / postal code';
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!paymentComponentRef.current || !cardReady) {
      setPageError("Payment form isn't ready yet — please wait a moment and try again.");
      return;
    }

    setSubmitting(true);
    const isValid = await paymentComponentRef.current.validate();
    if (!isValid) {
      setSubmitting(false);
      return;
    }
    await paymentComponentRef.current.confirm();
  };

  if (isNoOpSwitch) {
    // Redirect effect above handles navigation — render nothing while it fires.
    return null;
  }

  if (!plan || !planId) {
    return (
      <MainDashboardWrapper>
        <div className={css.base}>
          <div className={css.notFound}>
            <h1 className={css.notFoundTitle}>We couldn't find that plan</h1>
            <p className={css.notFoundText}>Please head back and pick a plan from the list.</p>
            <a className={css.backLink} href="/account/payments/pricing">
              &larr; Back to plans
            </a>
          </div>
        </div>
      </MainDashboardWrapper>
    );
  }

  const priceLabel = tier === 'lifetime' ? plan.price.oneTime : plan.price[period];
  const priceUnit = tier === 'lifetime' ? ' one-time' : isAnnual ? '/year' : '/month';
  const soldOut = tier === 'lifetime' && lifetime.soldOut;

  const actionVerb = isSwitch ? 'Switch to' : 'Subscribe to';

  return (
    <MainDashboardWrapper title={`${actionVerb} ${plan.title}`} page="checkout-plan">
      <div className={css.base}>
        <a className={css.backLink} href="/account/payments/pricing">
          &larr; Back to plans
        </a>

        <div className={css.checkoutLayout}>
          <div className={css.leftCol}>
            <span className={css.badge}>{plan.badge}</span>
            <h1 className={css.title}>{plan.title}</h1>
            <div className={css.price}>
              {priceLabel}
              <span className={css.priceUnit}>{priceUnit}</span>
            </div>
            {tier !== 'lifetime' && isAnnual && <div className={css.savingsNote}>{plan.savingsNote}</div>}
            {tier === 'lifetime' && lifetime.seatsRemaining !== null && !soldOut && (
              <div className={css.seatNote}>{lifetime.seatsRemaining} of 100 seats remaining</div>
            )}
            <p className={css.description}>
              {plan.descriptionKicker && <span className={css.descriptionKicker}>{plan.descriptionKicker} </span>}
              {plan.description}
            </p>
            <ul className={css.list}>
              {plan.features.map(feature => (
                <li key={feature.title}>
                  <span className={css.featureText}>
                    <span className={css.featureTitle}>{feature.title}</span> — {feature.detail}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className={css.rightCol}>
            <div className={css.summaryCard}>
              <h2 className={css.summaryTitle}>{actionVerb} {plan.title}</h2>

              {pageError && <div className={css.checkoutError}>{pageError}</div>}

              {soldOut ? (
                <div className={css.soldOut}>This plan is sold out.</div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className={css.formRow}>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="cpl-first-name">
                        First name
                      </label>
                      <input
                        id="cpl-first-name"
                        className={css.textInput}
                        value={form.firstName}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, firstName: value }));
                        }}
                      />
                      {formErrors.firstName && <div className={css.fieldError}>{formErrors.firstName}</div>}
                    </div>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="cpl-last-name">
                        Last name
                      </label>
                      <input
                        id="cpl-last-name"
                        className={css.textInput}
                        value={form.lastName}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, lastName: value }));
                        }}
                      />
                      {formErrors.lastName && <div className={css.fieldError}>{formErrors.lastName}</div>}
                    </div>
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="cpl-email">
                      Email
                    </label>
                    <input
                      id="cpl-email"
                      type="email"
                      className={css.textInput}
                      value={form.email}
                      readOnly
                      disabled
                      title="Your account email — sign in with a different account to change it"
                    />
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="cpl-address">
                      Billing address
                    </label>
                    <input
                      id="cpl-address"
                      className={css.textInput}
                      value={form.addressLine1}
                      onChange={e => {
                        const value = e.target.value;
                        setForm(f => ({ ...f, addressLine1: value }));
                      }}
                      placeholder="Street address"
                    />
                    {formErrors.addressLine1 && <div className={css.fieldError}>{formErrors.addressLine1}</div>}
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="cpl-country">
                      Country
                    </label>
                    <select
                      id="cpl-country"
                      className={css.textInput}
                      value={form.country || 'US'}
                      onChange={e => {
                        const value = e.target.value;
                        setForm(f => ({ ...f, country: value }));
                      }}
                    >
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={css.formRow}>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="cpl-city">
                        City
                      </label>
                      <input
                        id="cpl-city"
                        className={css.textInput}
                        value={form.city}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, city: value }));
                        }}
                      />
                      {formErrors.city && <div className={css.fieldError}>{formErrors.city}</div>}
                    </div>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="cpl-state">
                        State
                      </label>
                      <input
                        id="cpl-state"
                        className={css.textInput}
                        value={form.state}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, state: value }));
                        }}
                      />
                      {formErrors.state && <div className={css.fieldError}>{formErrors.state}</div>}
                    </div>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="cpl-zip">
                        ZIP / Postal code
                      </label>
                      <input
                        id="cpl-zip"
                        className={css.textInput}
                        value={form.zip}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, zip: value }));
                        }}
                      />
                      {formErrors.zip && <div className={css.fieldError}>{formErrors.zip}</div>}
                    </div>
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="cpl-coupon">
                      Coupon code
                    </label>
                    <div className={css.couponRow}>
                      <input
                        id="cpl-coupon"
                        className={css.textInput}
                        value={couponCode}
                        onChange={e => {
                          const value = e.target.value;
                          setCouponCode(value);
                          setCouponPreview(null);
                          setCouponError(null);
                          setCouponEligiblePlans(null);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            applyCoupon();
                          }
                        }}
                        placeholder="Optional"
                        disabled={!!couponPreview}
                      />
                      <button
                        type="button"
                        className={css.couponApplyBtn}
                        onClick={applyCoupon}
                        disabled={couponChecking || !couponCode.trim() || !!couponPreview}
                      >
                        {couponChecking ? 'Checking…' : 'Apply'}
                      </button>
                    </div>
                    {couponError && <div className={css.fieldError}>{couponError}</div>}
                    {couponEligiblePlans && couponEligiblePlans.length > 0 && (
                      <div className={css.couponEligiblePlans}>
                        {couponEligiblePlans.map(p => (
                          <a
                            key={`${p.tier}-${p.period}`}
                            className={css.couponSwitchPlanBtn}
                            href={`/account/checkout-plan/${p.tier}?period=${p.period}`}
                          >
                            Switch to {p.label}
                          </a>
                        ))}
                      </div>
                    )}
                    {couponPreview && (
                      <div className={css.couponSavings}>
                        Coupon applied — you save{' '}
                        {formatMoney(couponPreview.discountAmount, couponPreview.currencyCode)}
                        {couponPreview.discountAmount > 0 && (
                          <>
                            {' '}
                            ({formatMoney(couponPreview.originalPrice, couponPreview.currencyCode)} &rarr;{' '}
                            {formatMoney(couponPreview.finalPrice, couponPreview.currencyCode)})
                          </>
                        )}{' '}
                        <button type="button" className={css.couponRemoveBtn} onClick={removeCoupon}>
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel}>Payment details</label>
                    <div className={css.paymentComponentWrap}>
                      <div id="checkout-plan-loggedin-payment-component" className={css.paymentComponentMount} />
                      {!cardReady && !pageError && (
                        <div className={css.paymentLoading}>
                          <span className={css.paymentSpinner} />
                          Loading secure payment form…
                        </div>
                      )}
                    </div>
                  </div>

                  <button type="submit" className={css.submitBtn} disabled={submitting || !!pageError}>
                    {submitting ? 'Processing…' : isSwitch ? 'Pay & Switch Plan' : 'Pay & Subscribe'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainDashboardWrapper>
  );
}

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return { currentUser };
};

const mapDispatchToProps = dispatch => ({
  onFetchCurrentUser: () => dispatch(fetchCurrentUser()),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(CheckoutPlanLoggedInPage);
